import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Papa from 'papaparse';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Edit, Trash2, Upload, ChevronDown, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invoices = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    invoice_number: '',
    amount: '',
    issue_date: '',
    due_date: '',
    status: 'pending',
    paid_amount: '0',
    description: '',
    project_name: '',
    retainage_type: '',
    retainage_value: '',
  });

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        paid_amount: parseFloat(formData.paid_amount || 0),
        issue_date: new Date(formData.issue_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        retainage_type: formData.retainage_type || null,
        retainage_value: formData.retainage_value ? parseFloat(formData.retainage_value) : null,
      };

      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, data);
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API}/invoices`, data);
        toast.success('Invoice created');
      }

      fetchInvoices();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      client_id: invoice.client_id,
      invoice_number: invoice.invoice_number,
      amount: invoice.amount.toString(),
      issue_date: format(new Date(invoice.issue_date), 'yyyy-MM-dd'),
      due_date: format(new Date(invoice.due_date), 'yyyy-MM-dd'),
      status: invoice.status,
      paid_amount: invoice.paid_amount.toString(),
      description: invoice.description || '',
      project_name: invoice.project_name || '',
      retainage_type: invoice.retainage_type || '',
      retainage_value: invoice.retainage_value ? invoice.retainage_value.toString() : '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      invoice_number: '',
      amount: '',
      issue_date: '',
      due_date: '',
      status: 'pending',
      paid_amount: '0',
      description: '',
      project_name: '',
      retainage_type: '',
      retainage_value: '',
    });
    setEditingInvoice(null);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setCsvErrors([]);
    setCsvData([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const validData = [];

        results.data.forEach((row, index) => {
          const rowNum = index + 2; // +2 for header and 0-index
          const rowErrors = [];

          // Required fields validation
          if (!row.invoice_number?.trim()) rowErrors.push('invoice_number is required');
          if (!row.amount || isNaN(parseFloat(row.amount))) rowErrors.push('valid amount is required');
          if (!row.issue_date) rowErrors.push('issue_date is required (YYYY-MM-DD)');
          if (!row.due_date) rowErrors.push('due_date is required (YYYY-MM-DD)');
          if (!row.client_id?.trim()) rowErrors.push('client_id is required');

          // Date format validation (must be YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (row.issue_date) {
            if (!dateRegex.test(row.issue_date)) {
              rowErrors.push(`issue_date must be YYYY-MM-DD format (found: ${row.issue_date})`);
            } else if (isNaN(Date.parse(row.issue_date))) {
              rowErrors.push('issue_date is not a valid date');
            }
          }
          if (row.due_date) {
            if (!dateRegex.test(row.due_date)) {
              rowErrors.push(`due_date must be YYYY-MM-DD format (found: ${row.due_date})`);
            } else if (isNaN(Date.parse(row.due_date))) {
              rowErrors.push('due_date is not a valid date');
            }
          }

          // Status validation
          const validStatuses = ['pending', 'paid', 'overdue', 'partial'];
          if (row.status && !validStatuses.includes(row.status.toLowerCase())) {
            rowErrors.push(`status must be one of: ${validStatuses.join(', ')}`);
          }

          // Paid amount validation
          if (row.paid_amount && isNaN(parseFloat(row.paid_amount))) {
            rowErrors.push('paid_amount must be a number');
          }

          // Retainage validation
          if (row.retainage_type && !['percent', 'fixed'].includes(row.retainage_type.toLowerCase())) {
            rowErrors.push('retainage_type must be "percent" or "fixed"');
          }
          if (row.retainage_value && isNaN(parseFloat(row.retainage_value))) {
            rowErrors.push('retainage_value must be a number');
          }

          if (rowErrors.length > 0) {
            errors.push(`Row ${rowNum}: ${rowErrors.join(', ')}`);
          } else {
            validData.push({
              client_id: row.client_id.trim(),
              invoice_number: row.invoice_number.trim(),
              amount: parseFloat(row.amount),
              issue_date: row.issue_date,
              due_date: row.due_date,
              status: row.status?.toLowerCase() || 'pending',
              paid_amount: row.paid_amount ? parseFloat(row.paid_amount) : 0,
              description: row.description?.trim() || '',
              project_name: row.project_name?.trim() || '',
              retainage_type: row.retainage_type?.trim().toLowerCase() || null,
              retainage_value: row.retainage_value ? parseFloat(row.retainage_value) : null,
            });
          }
        });

        setCsvErrors(errors);
        setCsvData(validData);

        if (errors.length === 0 && validData.length > 0) {
          toast.success(`Parsed ${validData.length} valid invoice(s)`);
        } else if (errors.length > 0) {
          toast.error(`Found ${errors.length} error(s) in CSV`);
        }
      },
      error: (error) => {
        toast.error(`CSV parsing error: ${error.message}`);
      },
    });
  };

  const handleCsvUpload = async () => {
    if (csvData.length === 0) {
      toast.error('No valid data to upload');
      return;
    }

    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/invoices/bulk`, {
        invoices: csvData,
      });

      toast.success(`Successfully created ${response.data.created} invoice(s)`);

      if (response.data.failed > 0) {
        // Show detailed error information
        const errorDetails = response.data.errors.map((err) =>
          `Invoice ${err.invoice_number}: ${err.error}`
        ).join('\n');

        toast.error(
          `${response.data.failed} invoice(s) failed to create:\n\n${errorDetails}`,
          { duration: 10000 }
        );
      }

      fetchInvoices();

      // Only close dialog if all invoices succeeded
      if (response.data.failed === 0) {
        setIsCsvDialogOpen(false);
        setCsvFile(null);
        setCsvData([]);
        setCsvErrors([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload invoices');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadCsvTemplate = () => {
    const template = `client_id,invoice_number,amount,issue_date,due_date,status,paid_amount,description,project_name,retainage_type,retainage_value
REPLACE-WITH-YOUR-CLIENT-ID,INV-001,25000.00,2025-01-15,2025-02-15,pending,0,Electrical work - Phase 1,Building A Construction,percent,10
REPLACE-WITH-YOUR-CLIENT-ID,INV-002,15000.00,2025-01-20,2025-02-20,paid,15000.00,Plumbing installation,Building A Construction,fixed,1500
REPLACE-WITH-YOUR-CLIENT-ID,INV-003,30000.00,2025-01-25,2025-02-25,pending,0,HVAC system installation,Building A Construction,,`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoice_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      overdue: 'bg-red-100 text-red-800 border-red-200',
      partial: 'bg-blue-100 text-blue-800 border-blue-200',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const client = clients.find((c) => c.id === invoice.client_id);
    const clientName = client?.name || '';
    return (
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 md:mb-8 gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl text-slate-900 mb-1 md:mb-2">Invoices</h1>
            <p className="text-sm md:text-base text-slate-600">Manage and track all invoices</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="add-invoice-button"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-none btn-primary flex-1 sm:flex-none"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Add Invoice</span>
                  <span className="sm:hidden">Add</span>
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-none">
                <DropdownMenuItem onClick={() => setIsDialogOpen(true)} className="cursor-pointer">
                  <Plus className="h-4 w-4 mr-2" />
                  Single Invoice
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsCsvDialogOpen(true)} className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="max-w-2xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {editingInvoice ? 'Edit Invoice' : 'New Invoice'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="invoice-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client *</Label>
                    <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })} required>
                      <SelectTrigger data-testid="invoice-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice Number *</Label>
                    <Input
                      data-testid="invoice-number-input"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input
                      data-testid="invoice-amount-input"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Paid Amount</Label>
                    <Input
                      data-testid="invoice-paid-amount-input"
                      type="number"
                      step="0.01"
                      value={formData.paid_amount}
                      onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                      className="rounded-sm border-2 h-10"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Issue Date *</Label>
                    <Input
                      data-testid="invoice-issue-date-input"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date *</Label>
                    <Input
                      data-testid="invoice-due-date-input"
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="invoice-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Project Name</Label>
                  <Input
                    data-testid="invoice-project-input"
                    value={formData.project_name}
                    onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                    className="rounded-sm border-2 h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    data-testid="invoice-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="rounded-sm border-2 h-10"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Retainage Type</Label>
                    <select
                      data-testid="invoice-retainage-type-select"
                      value={formData.retainage_type}
                      onChange={(e) => setFormData({ ...formData, retainage_type: e.target.value })}
                      className="flex h-10 w-full rounded-sm border-2 border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-950"
                    >
                      <option value="">None</option>
                      <option value="percent">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Retainage Value</Label>
                    <Input
                      data-testid="invoice-retainage-value-input"
                      type="number"
                      step="0.01"
                      value={formData.retainage_value}
                      onChange={(e) => setFormData({ ...formData, retainage_value: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      placeholder={formData.retainage_type === 'percent' ? 'e.g., 10 for 10%' : 'e.g., 5000.00'}
                      disabled={!formData.retainage_type}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button type="submit" data-testid="invoice-save-button" className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-none">
                    {editingInvoice ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    data-testid="invoice-cancel-button"
                    variant="outline"
                    onClick={() => { setIsDialogOpen(false); resetForm(); }}
                    className="flex-1 rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* CSV Upload Dialog */}
          <Dialog open={isCsvDialogOpen} onOpenChange={setIsCsvDialogOpen}>
            <DialogContent className="max-w-3xl rounded-none max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Upload Invoices CSV</DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Template Download */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-none">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-1">Need a template?</h4>
                      <p className="text-sm text-slate-600">
                        Download our CSV template to see the required format and fields.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadCsvTemplate}
                      className="rounded-none border-slate-300"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Select CSV File</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileChange}
                    className="rounded-sm border-2 h-12 cursor-pointer"
                  />
                  {csvFile && (
                    <p className="text-sm text-slate-600">
                      Selected: <span className="font-medium">{csvFile.name}</span>
                    </p>
                  )}
                </div>

                {/* Validation Errors */}
                {csvErrors.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-none">
                    <h4 className="font-semibold text-red-900 mb-2">Validation Errors ({csvErrors.length})</h4>
                    <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                      {csvErrors.slice(0, 10).map((error, idx) => (
                        <li key={idx} className="font-mono-data text-xs">• {error}</li>
                      ))}
                      {csvErrors.length > 10 && (
                        <li className="text-red-600 font-semibold">
                          ... and {csvErrors.length - 10} more error(s)
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Preview */}
                {csvData.length > 0 && csvErrors.length === 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-none">
                    <h4 className="font-semibold text-green-900 mb-2">
                      Ready to Upload ({csvData.length} invoice{csvData.length !== 1 ? 's' : ''})
                    </h4>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-green-100 sticky top-0">
                          <tr className="text-left">
                            <th className="p-2 font-semibold">Invoice #</th>
                            <th className="p-2 font-semibold">Amount</th>
                            <th className="p-2 font-semibold">Issue Date</th>
                            <th className="p-2 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-green-200">
                          {csvData.map((invoice, idx) => (
                            <tr key={idx} className="hover:bg-green-100">
                              <td className="p-2 font-mono-data">{invoice.invoice_number}</td>
                              <td className="p-2 font-mono-data">${invoice.amount.toLocaleString()}</td>
                              <td className="p-2 font-mono-data">{invoice.issue_date}</td>
                              <td className="p-2">
                                <span className="px-2 py-1 text-xs bg-white border border-green-300 rounded-sm uppercase">
                                  {invoice.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* CSV Format Guide */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-none text-sm">
                  <h4 className="font-semibold text-slate-900 mb-2">CSV Format Requirements:</h4>
                  <ul className="text-slate-600 space-y-1">
                    <li>• <strong>Required fields:</strong> client_id, invoice_number, amount, issue_date, due_date</li>
                    <li>• <strong>Optional fields:</strong> status, paid_amount, description, project_name, retainage_type, retainage_value</li>
                    <li>• <strong>Date format:</strong> YYYY-MM-DD (e.g., 2024-01-15)</li>
                    <li>• <strong>Status values:</strong> pending, paid, overdue, partial</li>
                    <li>• <strong>Retainage type:</strong> percent or fixed (leave blank for no retainage)</li>
                    <li>• <strong>Retainage value:</strong> Number (e.g., 10 for 10% or 1500 for $1,500)</li>
                    <li>• <strong>Note:</strong> Use the client ID from your existing clients</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleCsvUpload}
                    disabled={csvData.length === 0 || csvErrors.length > 0 || isUploading}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-none"
                  >
                    {isUploading ? 'Uploading...' : `Upload ${csvData.length} Invoice${csvData.length !== 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCsvDialogOpen(false);
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvErrors([]);
                    }}
                    className="flex-1 rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="invoice-search-input"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm border-2 h-10"
            />
          </div>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="border border-slate-200 rounded-none shadow-none">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No invoices found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="invoices-list">
            {filteredInvoices.map((invoice) => {
              const client = clients.find((c) => c.id === invoice.client_id);
              const dueDate = new Date(invoice.due_date);
              const isOverdue = dueDate < new Date() && invoice.status !== 'paid';
              
              return (
                <Card key={invoice.id} className="border border-slate-200 rounded-none shadow-none" data-testid={`invoice-card-${invoice.id}`}>
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                      <div className="flex-1 w-full">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-mono-data text-base md:text-lg font-bold text-slate-900">
                            {invoice.invoice_number}
                          </h3>
                          <span className={`px-2 md:px-3 py-1 text-xs font-bold uppercase tracking-widest border rounded-none ${getStatusColor(invoice.status)}`}>
                            {invoice.status}
                          </span>
                          {isOverdue && invoice.status !== 'overdue' && (
                            <span className="px-2 md:px-3 py-1 text-xs font-bold uppercase tracking-widest border rounded-none bg-red-100 text-red-800 border-red-200">
                              OVERDUE
                            </span>
                          )}
                        </div>
                        <p className="text-sm md:text-base text-slate-600 mb-1">{client?.name || 'Unknown Client'}</p>
                        {invoice.project_name && (
                          <p className="text-xs md:text-sm text-slate-500 mb-2">Project: {invoice.project_name}</p>
                        )}
                        <div className="flex flex-col sm:flex-row sm:gap-6 gap-2 text-xs md:text-sm">
                          <div>
                            <span className="text-slate-500">Amount: </span>
                            <span className="font-mono-data font-bold text-slate-900">${invoice.amount.toLocaleString()}</span>
                          </div>
                          {invoice.retainage_amount && invoice.retainage_amount > 0 && (
                            <>
                              <div>
                                <span className="text-slate-500">Retainage: </span>
                                <span className="font-mono-data text-orange-600">-${invoice.retainage_amount.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Net: </span>
                                <span className="font-mono-data font-bold text-slate-900">${invoice.net_invoice_amount.toLocaleString()}</span>
                              </div>
                            </>
                          )}
                          <div>
                            <span className="text-slate-500">Due: </span>
                            <span className="font-mono-data text-slate-900">{format(dueDate, 'MMM dd, yyyy')}</span>
                          </div>
                          {invoice.paid_amount > 0 && (
                            <div>
                              <span className="text-slate-500">Paid: </span>
                              <span className="font-mono-data text-green-600">${invoice.paid_amount.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                          data-testid={`edit-invoice-${invoice.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(invoice)}
                          className="rounded-none border-slate-200 hover:border-slate-900 flex-1 sm:flex-none"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-invoice-${invoice.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(invoice.id)}
                          className="rounded-none border-slate-200 hover:border-red-600 hover:text-red-600 flex-1 sm:flex-none"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Invoices;